import type { ReactNode } from "react";
import { Crown, Pencil, WalletCards } from "lucide-react";
import { Modal } from "../Modal";
import { Pairs, RecordSheet, SectionBar } from "../RecordSections";
import { formatSchoolDate } from "../students/studentDisplay";
import type { StaffProfile } from "../../services/staffService";
import { StaffStatus } from "./StaffStatus";

type Props = {
  profile: StaffProfile;
  canManage: boolean;
  onEdit: () => void;
  onPayChange: () => void;
  onClose: () => void;
};

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || "?") + (parts[1]?.[0] || "")).toUpperCase();
}

/** Whole years since a YYYY-MM-DD date. */
export function yearsOfService(hireDate: string, today = new Date()) {
  const hired = new Date(`${hireDate.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(hired.getTime())) return null;
  let years = today.getFullYear() - hired.getFullYear();
  if (
    today.getMonth() < hired.getMonth() ||
    (today.getMonth() === hired.getMonth() && today.getDate() < hired.getDate())
  )
    years -= 1;
  return Math.max(0, years);
}

/** Only the last four digits of an account number are shown. */
function masked(accountNumber?: string) {
  if (!accountNumber) return "";
  return accountNumber.length > 4 ? `•••• ${accountNumber.slice(-4)}` : accountNumber;
}

export default function StaffProfileModal({
  profile,
  canManage,
  onEdit,
  onPayChange,
  onClose,
}: Props) {
  const money = (amount?: string | null) =>
    amount && Number(amount) > 0
      ? `${profile.currency} ${Number(amount).toLocaleString()}`
      : "";
  const allowances = Number(profile.allowances ?? 0);
  const gross = Number(profile.salary ?? 0) + allowances;
  const service = yearsOfService(profile.hire_date);
  const changes = profile.employment_changes ?? [];

  return (
    <Modal
      isOpen
      size="4xl"
      title="Staff record"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline">
            Close
          </button>
          {canManage && (
            <>
              <button type="button" onClick={onPayChange} className="btn btn-outline gap-2">
                <WalletCards className="h-4 w-4" />
                {profile.salary ? "Request pay change" : "Set pay"}
              </button>
              <button type="button" onClick={onEdit} className="btn btn-primary gap-2">
                <Pencil className="h-4 w-4" /> Edit employment
              </button>
            </>
          )}
        </>
      }
    >
      <RecordSheet>
        <header className="flex flex-wrap items-center gap-3 px-4 py-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
            {initials(profile.user_name)}
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold leading-6 text-gray-900">{profile.user_name}</h2>
            <p className="font-mono text-xs text-gray-500">
              {profile.employee_id} · {profile.user_username}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5 text-xs font-medium">
            <span className="inline-flex items-center rounded border border-gray-200 px-2 py-0.5">
              <StaffStatus status={profile.employment_status} />
            </span>
            <span className="rounded bg-primary-50 px-2 py-0.5 text-primary-700">
              {profile.position_title}
            </span>
            <span className="rounded bg-gray-100 px-2 py-0.5 text-gray-700">
              {profile.department_name || "School-wide"}
            </span>
            {profile.headed_departments?.map((department) => (
              <span
                key={department.id}
                className="inline-flex items-center gap-1 rounded bg-amber-50 px-2 py-0.5 text-amber-800"
              >
                <Crown className="h-3 w-3" /> Heads {department.name}
              </span>
            ))}
          </div>
        </header>

        <SectionBar letter="A" title="Employment" />
        <Pairs
          rows={[
            ["Position", profile.position_title],
            ["Department", profile.department_name || "School-wide"],
            [
              "Hired",
              profile.hire_date &&
                `${formatSchoolDate(profile.hire_date)}${service !== null ? ` (${service} yr${service === 1 ? "" : "s"})` : ""}`,
            ],
            ["Work location", profile.work_location],
            ["Reports to", profile.reporting_manager_name],
            [
              "Heads",
              profile.headed_departments?.map((department) => department.name).join(", "),
            ],
          ]}
        />

        <SectionBar letter="B" title="Pay" />
        <Pairs
          rows={[
            ["Basic salary", money(profile.salary)],
            ["Paid", <span className="capitalize">{profile.payment_frequency}</span>],
            [
              "Allowances",
              allowances > 0 &&
                `${money(profile.allowances)}${profile.allowance_lines?.length ? ` (${profile.allowance_lines.map((line) => line.name).join(", ")})` : ""}`,
            ],
            ["Gross pay", gross > 0 && money(String(gross))],
            ...(canManage
              ? ([
                  ["TIN", profile.tin && <span className="font-mono">{profile.tin}</span>],
                  ["NSSF no.", profile.nssf_number && <span className="font-mono">{profile.nssf_number}</span>],
                  [
                    "Tax residency",
                    profile.tax_residency && (profile.tax_residency === "resident" ? "Resident" : "Non-resident"),
                  ],
                  [
                    "Bank",
                    [profile.bank_name, profile.bank_branch].filter(Boolean).join(", "),
                  ],
                  ["Account name", profile.bank_account_name],
                  [
                    "Account no.",
                    profile.bank_account_number && (
                      <span className="font-mono">{masked(profile.bank_account_number)}</span>
                    ),
                  ],
                ] as [string, ReactNode][])
              : []),
          ]}
        />

        <SectionBar letter="C" title="Employment history" />
        <div className="overflow-x-auto px-4 py-3">
          {changes.length ? (
            <table className="w-full text-left">
              <thead className="border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="w-8 py-1 pr-3 font-semibold">No.</th>
                  <th className="py-1 pr-3 font-semibold">Effective</th>
                  <th className="py-1 pr-3 font-semibold">Change</th>
                  <th className="py-1 pr-3 font-semibold">Reason</th>
                  <th className="py-1 font-semibold">By</th>
                </tr>
              </thead>
              <tbody>
                {changes.map((change, index) => (
                  <tr key={change.id} className="border-b border-gray-100 align-top last:border-0">
                    <td className="py-1.5 pr-3 text-gray-500">{index + 1}.</td>
                    <td className="whitespace-nowrap py-1.5 pr-3 tabular-nums">
                      {formatSchoolDate(change.effective_date)}
                    </td>
                    <td className="whitespace-nowrap py-1.5 pr-3 capitalize">
                      {change.previous_status.replace(/_/g, " ")} → {change.new_status.replace(/_/g, " ")}
                    </td>
                    <td className="py-1.5 pr-3 text-gray-700">{change.reason}</td>
                    <td className="whitespace-nowrap py-1.5 text-gray-600">{change.changed_by_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-500">No employment changes recorded.</p>
          )}
        </div>
      </RecordSheet>
    </Modal>
  );
}
